import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import shp from "shpjs";
import * as toGeoJSON from "@tmcw/togeojson";
import JSZip from "jszip";
import tokml from "tokml";
import * as shpwrite from "@mapbox/shp-write";
import { toast } from "sonner";
import { Upload, Layers, Trash2, Map as MapIcon, Satellite, Loader2, X, Crosshair, Maximize, Download, PenLine, Copy, FileSpreadsheet, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Package, Gem, ChevronUp, ChevronDown, BarChart3, MapPin } from "lucide-react";
import { repemCSV, buildVertices, validateAnm, adjustRepem } from "../lib/anmRepem";

const PALETTE = ["#e8590c", "#1971c2", "#2f9e44", "#9c36b5", "#c2255c", "#f08c00", "#0c8599", "#5f3dc4"];

// Cores por substância (leitura rápida no mapa). As não mapeadas caem em "Outras".
const SUBST_COLORS = {
    "OURO": "#f59f00", "CALCÁRIO": "#adb5bd", "AREIA": "#fcc419", "ARGILA": "#e8590c",
    "GRANITO": "#868e96", "TUNGSTÊNIO": "#7048e8", "FERRO": "#e03131", "MANGANÊS": "#9c36b5",
    "DIAMANTE": "#22b8cf", "ESTANHO": "#4263eb", "BASALTO": "#495057", "COBRE": "#d9480f",
};
const OUTRAS_COLOR = "#94a3b8";
const substColor = (name) => SUBST_COLORS[(name || "").toUpperCase()] || OUTRAS_COLOR;
const hexTextColor = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#fff";
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? "#1f2937" : "#fff";
};
const ICON_BASE = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images";
const R_EARTH = 6378137;

function geodesicAreaM2(latlngs) {
    const n = latlngs.length;
    if (n < 3) return 0;
    const d2r = Math.PI / 180;
    let area = 0;
    for (let i = 0; i < n; i++) {
        const p1 = latlngs[i];
        const p2 = latlngs[(i + 1) % n];
        area += (p2.lng - p1.lng) * d2r * (2 + Math.sin(p1.lat * d2r) + Math.sin(p2.lat * d2r));
    }
    return Math.abs(area * R_EARTH * R_EARTH / 2.0);
}
function lineLenM(latlngs) {
    let s = 0;
    for (let i = 1; i < latlngs.length; i++) s += latlngs[i - 1].distanceTo(latlngs[i]);
    return s;
}
function fmtLen(m) { return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(0)} m`; }
function fmtHa(m2) { return `${(m2 / 10000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ha`; }
function ringOf(layer) { const lls = layer.getLatLngs(); return Array.isArray(lls[0]) ? lls[0] : lls; }
function toDMS(deg, isLat) {
    if (deg === undefined || deg === null || isNaN(deg)) return "—";
    const hemi = isLat ? (deg >= 0 ? "N" : "S") : (deg >= 0 ? "L" : "O");
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mf = (abs - d) * 60;
    const m = Math.floor(mf);
    const s = ((mf - m) * 60).toFixed(2);
    return `${d}°${String(m).padStart(2, "0")}'${s.padStart(5, "0")}"${hemi}`;
}
function centroidOf(layer) {
    try { return (layer instanceof L.Marker) ? layer.getLatLng() : layer.getCenter(); } catch (e) { return null; }
}
function pointInRing(pt, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i].lng, yi = ring[i].lat, xj = ring[j].lng, yj = ring[j].lat;
        const intersect = ((yi > pt.lat) !== (yj > pt.lat)) && (pt.lng < (xj - xi) * (pt.lat - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}
function distPointToSegPx(p, a, b) {
    const dx = b.x - a.x, dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
function vertexList(ring) {
    return ring.map((p, i) => ({
        i: i + 1,
        lat: p.lat, lng: p.lng,
        dms: `${toDMS(p.lat, true)} ${toDMS(p.lng, false)}`,
        dec: `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}`,
    }));
}
function measureLayer(layer) {
    const c = centroidOf(layer);
    const cen = c ? { lat: c.lat, lng: c.lng } : null;
    const cenStr = cen ? `${cen.lat.toFixed(6)}, ${cen.lng.toFixed(6)}` : "—";
    if (layer instanceof L.Polygon) {
        const ring = ringOf(layer);
        const a = geodesicAreaM2(ring);
        const per = ring.length ? lineLenM([...ring, ring[0]]) : 0;
        const rows = [
            ["Tipo", "Polígono"],
            ["Área", `${fmtHa(a)} (${(a / 1e6).toFixed(3)} km²)`],
            ["Perímetro", fmtLen(per)],
            ["Centroide", cenStr],
            ["Vértices", String(ring.length)],
        ];
        return { type: "polygon", text: `Área: ${fmtHa(a)} · Perím.: ${fmtLen(per)}`, rows, centroid: cen, vertices: vertexList(ring) };
    }
    if (layer instanceof L.Polyline) {
        const ring = ringOf(layer);
        const len = lineLenM(ring);
        const rows = [
            ["Tipo", "Linha"],
            ["Comprimento", fmtLen(len)],
            ["Centro", cenStr],
            ["Vértices", String(ring.length)],
        ];
        return { type: "line", text: `Comprimento: ${fmtLen(len)}`, rows, centroid: cen, vertices: vertexList(ring) };
    }
    if (layer instanceof L.Marker) {
        const p = layer.getLatLng();
        const rows = [
            ["Tipo", "Ponto"],
            ["Coordenada", `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}`],
            ["DMS", `${toDMS(p.lat, true)} ${toDMS(p.lng, false)}`],
        ];
        return { type: "point", text: `Ponto: ${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`, rows, centroid: { lat: p.lat, lng: p.lng }, vertices: vertexList([p]) };
    }
    return { type: "?", text: "", rows: [], centroid: null, vertices: [] };
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: `${ICON_BASE}/marker-icon-2x.png`,
    iconUrl: `${ICON_BASE}/marker-icon.png`,
    shadowUrl: `${ICON_BASE}/marker-shadow.png`,
});

export default function GeoIntel() {
    const mapRef = useRef(null);
    const mapObj = useRef(null);
    const baseLayers = useRef({});
    const layerStore = useRef({});
    const drawnRef = useRef(null);
    const fileInput = useRef(null);
    const infoActiveRef = useRef(false);
    const toolBtns = useRef({});
    const locateLayer = useRef(null);
    const recomputeRef = useRef(null);
    const [locating, setLocating] = useState(false);
    const [basemap, setBasemap] = useState("mapa");
    const [layers, setLayers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [exportAsk, setExportAsk] = useState(null);
    const [coord, setCoord] = useState(null);
    const [elev, setElev] = useState(null);
    const [drawCount, setDrawCount] = useState(0);
    const [measures, setMeasures] = useState([]);
    const [anm, setAnm] = useState(null);
    const [live, setLive] = useState(null);
    // Camada Recursos Minerais: features cruas + estado de legenda/filtro por substância.
    const recursoFeaturesRef = useRef(null);
    const recursoVisibleRef = useRef(true);
    const [substList, setSubstList] = useState([]); // [{name,count}]
    const [selectedSubs, setSelectedSubs] = useState(null); // Set | null
    const [substSearch, setSubstSearch] = useState("");
    const [substOpen, setSubstOpen] = useState(true);
    const [viewSummary, setViewSummary] = useState([]); // [{name,count}] na área visível
    const [viewTotal, setViewTotal] = useState(0);
    const [viewSumOpen, setViewSumOpen] = useState(true);
    const [coordModal, setCoordModal] = useState(false);
    const [coordMode, setCoordMode] = useState("decimal"); // decimal | dms
    const [decForm, setDecForm] = useState({ lat: "", lng: "" });
    const [dmsForm, setDmsForm] = useState({ latD: "", latM: "", latS: "", latH: "N", lngD: "", lngM: "", lngS: "", lngH: "O" });
    const [coordErr, setCoordErr] = useState("");

    useEffect(() => {
        if (mapObj.current || !mapRef.current) return;
        const map = L.map(mapRef.current, { center: [-14.2, -51.9], zoom: 4, zoomControl: true, preferCanvas: true });
        const osm = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" });
        const sat = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19, attribution: "Tiles © Esri — World Imagery" });
        osm.addTo(map);
        baseLayers.current = { mapa: osm, satelite: sat };
        mapObj.current = map;
        if (typeof window !== "undefined") window.__geoMap = map;

        // ---- Ferramentas de desenho (ponto / linha / polígono) ----
        const drawn = L.featureGroup().addTo(map);
        drawnRef.current = drawn;
        // Estado da ferramenta de polígono ortogonal (lados N-S / L-O, ângulos de 90°).
        let orthoActive = false;
        let orthoPts = [];
        let orthoPreview = null;
        const orthoDots = L.layerGroup();
        try {
            map.pm.addControls({
                position: "topleft",
                drawMarker: true, drawPolyline: true, drawPolygon: false,
                drawCircle: false, drawCircleMarker: false, drawRectangle: false, drawText: false,
                editMode: true, dragMode: false, removalMode: true, rotateMode: false, cutPolygon: false,
            });
            if (map.pm.setLang) map.pm.setLang("pt_br");
        } catch (e) { /* geoman */ }

        // ---- Barra própria: Pan (padrão) + Info (identificar) ----
        const setActiveBtn = (which) => {
            ["pan", "info", "ortho"].forEach((k) => {
                const el = toolBtns.current[k];
                if (el) el.classList.toggle("geo-tool-active", k === which);
            });
        };
        // ---- Polígono ortogonal: snap dos lados aos eixos lat/long (paralelos e meridianos) ----
        const metersToDeg = (m, axis, lat) => axis === "lat" ? m / 110540 : m / (111320 * Math.cos(lat * Math.PI / 180) || 1);
        const snapVertices = () => {
            const out = [];
            const collect = (s) => {
                if (s.eachLayer) { s.eachLayer(collect); return; }
                if (s.getLatLng) out.push(s.getLatLng());
                else if (s.getLatLngs) {
                    const flat = [];
                    const walk = (a) => a.forEach((x) => (Array.isArray(x) ? walk(x) : flat.push(x)));
                    walk(s.getLatLngs()); out.push(...flat);
                }
            };
            Object.values(layerStore.current).forEach(collect);
            return out;
        };
        // Retorna {ll, freeAxis} — mantém o eixo travado do vértice anterior e move só o eixo livre.
        const orthoAxis = (from, to) => {
            const a = map.latLngToContainerPoint(from);
            const b = map.latLngToContainerPoint(to);
            return Math.abs(b.x - a.x) >= Math.abs(b.y - a.y)
                ? { ll: L.latLng(from.lat, to.lng), freeAxis: "lng" }   // segmento L-O (mesmo paralelo)
                : { ll: L.latLng(to.lat, from.lng), freeAxis: "lat" };  // segmento N-S (mesmo meridiano)
        };
        // Aderência com folga (~5 m) aos vértices de outras feições, apenas no eixo livre.
        const orthoSnapGap = 5;
        const applyGapSnap = (from, ll, freeAxis) => {
            const cands = snapVertices();
            if (!cands.length) return ll;
            const gp = map.latLngToContainerPoint(ll);
            let best = null, bestd = Infinity;
            cands.forEach((c) => {
                const cp = map.latLngToContainerPoint(c);
                const d = freeAxis === "lng" ? Math.abs(cp.x - gp.x) : Math.abs(cp.y - gp.y);
                if (d < bestd) { bestd = d; best = c; }
            });
            if (!best || bestd > 18) return ll;
            if (freeAxis === "lng") {
                const dir = Math.sign(best.lng - from.lng) || 1;
                return L.latLng(from.lat, best.lng - dir * metersToDeg(orthoSnapGap, "lng", from.lat));
            }
            const dir = Math.sign(best.lat - from.lat) || 1;
            return L.latLng(best.lat - dir * metersToDeg(orthoSnapGap, "lat", from.lat), from.lng);
        };
        const snapOrtho = (from, to) => {
            const r = orthoAxis(from, to);
            return applyGapSnap(from, r.ll, r.freeAxis);
        };
        const orthoCleanup = () => {
            orthoPts = [];
            if (orthoPreview) { map.removeLayer(orthoPreview); orthoPreview = null; }
            orthoDots.clearLayers();
        };
        const orthoRedraw = (cursor) => {
            const ghost = (cursor && orthoPts.length) ? snapOrtho(orthoPts[orthoPts.length - 1], cursor) : null;
            const line = ghost ? [...orthoPts, ghost] : orthoPts.slice();
            if (!line.length) { if (orthoPreview) orthoPreview.setLatLngs([]); return; }
            if (!orthoPreview) orthoPreview = L.polyline(line, { color: "#1971c2", weight: 2, dashArray: "5 4" }).addTo(map);
            else orthoPreview.setLatLngs(line);
            orthoDots.clearLayers();
            orthoPts.forEach((p, i) => L.circleMarker(p, { radius: i === 0 ? 6 : 4, color: "#1971c2",
                fillColor: "#fff", fillOpacity: 1, weight: 2 }).addTo(orthoDots));
            orthoDots.addTo(map);
            // Área/perímetro acumulados ao vivo, para dimensionar a poligonal.
            if (orthoActive) {
                if (line.length >= 3) {
                    const a = geodesicAreaM2(line);
                    setLive({ shape: "OrthoPolygon", text: `Área: ${fmtHa(a)} · Perímetro: ${fmtLen(lineLenM(line))} · ${orthoPts.length} vértice(s). Duplo-clique ou 1º ponto p/ fechar.` });
                } else if (line.length === 2) {
                    setLive({ shape: "OrthoPolygon", text: `Lado: ${fmtLen(lineLenM(line))} · continue clicando (lados N-S / L-O).` });
                } else {
                    setLive({ shape: "OrthoPolygon", text: "Clique para o próximo vértice (lados N-S / L-O)." });
                }
            }
        };
        const orthoLastAxis = () => {
            if (orthoPts.length < 2) return null;
            const a = orthoPts[orthoPts.length - 2], b = orthoPts[orthoPts.length - 1];
            return Math.abs(b.lng - a.lng) >= Math.abs(b.lat - a.lat) ? "h" : "v";
        };
        const orthoAdd = (latlng) => {
            orthoPts.push(orthoPts.length ? snapOrtho(orthoPts[orthoPts.length - 1], latlng) : latlng);
            orthoRedraw();
        };
        const orthoFinish = () => {
            // Remove vértices consecutivos coincidentes (ex.: o clique extra do duplo-clique).
            const pts = [];
            orthoPts.forEach((p) => {
                const prev = pts[pts.length - 1];
                if (!prev || Math.abs(prev.lat - p.lat) > 1e-9 || Math.abs(prev.lng - p.lng) > 1e-9) pts.push(p);
            });
            orthoPts = pts;
            if (orthoPts.length < 3) { toast.error("Adicione ao menos 3 vértices para fechar o polígono."); return; }
            const first = orthoPts[0], last = orthoPts[orthoPts.length - 1];
            const ring = orthoPts.slice();
            const sameLat = Math.abs(first.lat - last.lat) < 1e-9;
            const sameLng = Math.abs(first.lng - last.lng) < 1e-9;
            if (!sameLat && !sameLng) {
                // Insere um vértice de canto para que os dois últimos lados também fiquem ortogonais.
                const corner = orthoLastAxis() === "h" ? L.latLng(first.lat, last.lng) : L.latLng(last.lat, first.lng);
                ring.push(corner);
            }
            const poly = L.polygon(ring, { color: "#1971c2", weight: 2, fillColor: "#1971c2", fillOpacity: 0.2 });
            drawn.addLayer(poly);
            poly.on("pm:edit pm:markerdragend pm:dragend pm:update", recompute);
            poly.on("pm:remove", () => { recompute(); refreshDraw(); });
            recompute(); refreshDraw();
            activatePan();
            toast.success("Polígono ortogonal criado.");
        };
        const activateOrtho = () => {
            try { map.pm.disableDraw(); } catch (er) { /* noop */ }
            try { if (map.pm.globalEditModeEnabled && map.pm.globalEditModeEnabled()) map.pm.disableGlobalEditMode(); } catch (er) { /* noop */ }
            try { if (map.pm.globalRemovalModeEnabled && map.pm.globalRemovalModeEnabled()) map.pm.disableGlobalRemovalMode(); } catch (er) { /* noop */ }
            infoActiveRef.current = false;
            orthoActive = true;
            orthoCleanup();
            map.doubleClickZoom.disable();
            map.dragging.enable();
            map.getContainer().style.cursor = "crosshair";
            setActiveBtn("ortho");
            setLive({ shape: "OrthoPolygon", text: "Clique para vértices (lados N-S / L-O). Duplo-clique ou clique no 1º ponto para fechar. Esc cancela." });
        };
        const deactivateOrtho = () => {
            if (!orthoActive) return;
            orthoActive = false;
            orthoCleanup();
            try { map.doubleClickZoom.enable(); } catch (er) { /* noop */ }
            setLive(null);
        };
        const activatePan = () => {
            try { map.pm.disableDraw(); } catch (er) { /* noop */ }
            try { if (map.pm.globalEditModeEnabled && map.pm.globalEditModeEnabled()) map.pm.disableGlobalEditMode(); } catch (er) { /* noop */ }
            try { if (map.pm.globalRemovalModeEnabled && map.pm.globalRemovalModeEnabled()) map.pm.disableGlobalRemovalMode(); } catch (er) { /* noop */ }
            deactivateOrtho();
            infoActiveRef.current = false;
            map.dragging.enable();
            map.getContainer().style.cursor = "grab";
            setActiveBtn("pan");
        };
        const activateInfo = () => {
            try { map.pm.disableDraw(); } catch (er) { /* noop */ }
            deactivateOrtho();
            infoActiveRef.current = true;
            map.getContainer().style.cursor = "help";
            setActiveBtn("info");
        };
        const locateMe = () => {
            if (!navigator.geolocation) { toast.error("Geolocalização não suportada neste navegador."); return; }
            setLocating(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setLocating(false);
                    const { latitude, longitude, accuracy } = pos.coords;
                    const ll = L.latLng(latitude, longitude);
                    if (locateLayer.current) { map.removeLayer(locateLayer.current); locateLayer.current = null; }
                    const grp = L.layerGroup();
                    L.circle(ll, { radius: accuracy || 30, color: "#1971c2", weight: 1, fillColor: "#1971c2", fillOpacity: 0.12 }).addTo(grp);
                    L.circleMarker(ll, { radius: 6, color: "#fff", weight: 2, fillColor: "#1971c2", fillOpacity: 1 })
                        .bindTooltip("Você está aqui", { direction: "top", offset: [0, -6] }).addTo(grp);
                    grp.addTo(map);
                    locateLayer.current = grp;
                    map.setView(ll, Math.max(map.getZoom(), 15), { animate: true });
                    toast.success(`Localização encontrada (±${Math.round(accuracy || 0)} m)`);
                },
                (err) => {
                    setLocating(false);
                    const msg = err.code === 1 ? "Permissão de localização negada." : err.code === 3 ? "Tempo esgotado ao obter localização." : "Não foi possível obter sua localização.";
                    toast.error(msg);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        };
        const ToolBar = L.Control.extend({
            options: { position: "topleft" },
            onAdd: function () {
                const bar = L.DomUtil.create("div", "leaflet-bar geo-toolbar");
                const mk = (key, title, testid, cls) => {
                    const a = L.DomUtil.create("a", cls, bar);
                    a.href = "#"; a.title = title; a.setAttribute("data-testid", testid);
                    L.DomEvent.on(a, "click", L.DomEvent.stop);
                    return a;
                };
                const pan = mk("pan", "Mover o mapa (Pan)", "geo-pan-btn", "geo-pan-icon");
                const ortho = mk("ortho", "Polígono ortogonal (lados N-S / L-O, 90°)", "geo-ortho-btn", "geo-ortho-icon");
                const info = mk("info", "Informações dos elementos", "geo-info-btn", "geo-info-icon");
                const locate = mk("locate", "Minha localização (GPS)", "geo-locate-btn", "geo-locate-icon");
                toolBtns.current = { pan, ortho, info, locate };
                L.DomEvent.on(pan, "click", activatePan);
                L.DomEvent.on(ortho, "click", () => (orthoActive ? activatePan() : activateOrtho()));
                L.DomEvent.on(info, "click", () => (infoActiveRef.current ? activatePan() : activateInfo()));
                L.DomEvent.on(locate, "click", locateMe);
                return bar;
            },
        });
        map.addControl(new ToolBar());

        // Ao escolher qualquer ferramenta do Geoman, Pan/Info/Ortho deixam de estar ativos
        const clearOwnTools = () => { infoActiveRef.current = false; deactivateOrtho(); setActiveBtn(null); map.getContainer().style.cursor = ""; };
        map.on("pm:drawstart", clearOwnTools);
        map.on("pm:globaleditmodetoggled", (e) => { if (e.enabled) clearOwnTools(); });
        map.on("pm:globalremovalmodetoggled", (e) => { if (e.enabled) clearOwnTools(); });

        // ---- Identificar: exibe atributos de TODOS os elementos sob o clique ----
        const identifyAt = (latlng) => {
            const clickPt = map.latLngToContainerPoint(latlng);
            const hits = [];
            const testSub = (sub, source) => {
                const feat = sub.feature;
                const props = (feat && feat.properties) || {};
                if (sub instanceof L.Polygon) {
                    const ring = ringOf(sub);
                    if (ring.length && pointInRing(latlng, ring)) hits.push({ source, type: "Polígono", props });
                } else if (sub instanceof L.Polyline) {
                    const pts = ringOf(sub).map((ll) => map.latLngToContainerPoint(ll));
                    let dmin = Infinity;
                    for (let i = 1; i < pts.length; i++) dmin = Math.min(dmin, distPointToSegPx(clickPt, pts[i - 1], pts[i]));
                    if (dmin <= 8) hits.push({ source, type: "Linha", props });
                } else if (sub.getLatLng) {
                    if (map.latLngToContainerPoint(sub.getLatLng()).distanceTo(clickPt) <= 12) hits.push({ source, type: "Ponto", props });
                }
            };
            const walk = (grp, source) => grp.eachLayer((s) => (s.eachLayer ? walk(s, source) : testSub(s, source)));
            Object.values(layerStore.current).forEach((gl) => walk(gl, gl._geoName || "Camada"));
            if (drawnRef.current) walk(drawnRef.current, "Desenho");
            if (!hits.length) {
                L.popup().setLatLng(latlng).setContent('<div style="font-size:12px;color:#64748b">Nenhum elemento neste ponto.</div>').openOn(map);
                return;
            }
            const block = (h) => {
                const keys = Object.keys(h.props).slice(0, 20);
                const rows = keys.length
                    ? keys.map((k) => `<tr><td style="color:#64748b;padding-right:8px;vertical-align:top">${k}</td><td style="color:#0f172a;font-weight:600">${String(h.props[k] ?? "").slice(0, 80)}</td></tr>`).join("")
                    : '<tr><td style="color:#94a3b8">sem atributos</td></tr>';
                return `<div style="margin-bottom:6px"><div style="font-weight:700;color:#0f172a;font-size:12px">${h.source} · ${h.type}</div><table style="font-size:11px">${rows}</table></div>`;
            };
            const html = `<div style="max-height:260px;overflow:auto;min-width:180px">${hits.map(block).join('<hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0"/>')}</div>`;
            L.popup({ maxWidth: 320 }).setLatLng(latlng).setContent(html).openOn(map);
        };
        const refreshDraw = () => setDrawCount(drawn.getLayers().length);
        const recompute = () => {
            const list = drawn.getLayers().map((l) => {
                const m = measureLayer(l);
                try {
                    if (m.type !== "point") {
                        l.bindTooltip(m.text, { permanent: true, direction: "center", className: "geo-measure-tip", opacity: 0.9 });
                        l.openTooltip();
                    }
                } catch (err) { /* tooltip */ }
                return { id: L.Util.stamp(l), color: l.options && l.options.color, ...m };
            });
            setMeasures(list);
        };
        recomputeRef.current = recompute;
        map.on("pm:create", (e) => {
            try { if (e.layer.setStyle) e.layer.setStyle({ color: "#1971c2", fillColor: "#1971c2" }); } catch (err) { /* estilo */ }
            drawn.addLayer(e.layer);
            e.layer.on("pm:edit pm:markerdragend pm:dragend pm:update", recompute);
            recompute();
            refreshDraw();
            e.layer.on("pm:remove", () => { recompute(); refreshDraw(); });
            activatePan();
        });
        map.on("pm:remove", (e) => { drawn.removeLayer(e.layer); recompute(); refreshDraw(); });

        // Medição ao vivo durante o desenho
        const computeLive = (shape, layer) => {
            let lls = [];
            try { const raw = layer.getLatLngs(); lls = Array.isArray(raw[0]) ? raw[0] : raw; } catch (err) { lls = []; }
            if (shape === "Line") return `Comprimento: ${fmtLen(lineLenM(lls))}`;
            if (shape === "Polygon") {
                const a = lls.length >= 3 ? geodesicAreaM2(lls) : 0;
                return `Área: ${fmtHa(a)} · Lados: ${fmtLen(lineLenM(lls))}`;
            }
            return "clique para posicionar o ponto";
        };
        map.on("pm:drawstart", (e) => {
            const shape = e.shape;
            const wl = e.workingLayer;
            setLive({ shape, text: shape === "Marker" ? "clique para posicionar o ponto" : "clique para adicionar vértices…" });
            if (wl && wl.on) {
                const upd = () => setLive({ shape, text: computeLive(shape, wl) });
                wl.on("pm:vertexadded", upd);
                wl.on("pm:change", upd);
            }
        });
        map.on("pm:drawend", () => setLive(null));
        activatePan();

        // ---- Coordenadas (lat/long em DMS) + elevação ----
        map.on("mousemove", (e) => {
            setCoord(e.latlng);
            if (orthoActive && orthoPts.length) orthoRedraw(e.latlng);
        });
        map.on("click", async (e) => {
            if (orthoActive) {
                // Fecha ao clicar próximo ao 1º vértice; caso contrário adiciona vértice ortogonal.
                if (orthoPts.length >= 3) {
                    const fp = map.latLngToContainerPoint(orthoPts[0]);
                    if (map.latLngToContainerPoint(e.latlng).distanceTo(fp) <= 12) { orthoFinish(); return; }
                }
                orthoAdd(e.latlng);
                return;
            }
            if (infoActiveRef.current) identifyAt(e.latlng);
            setElev("…");
            try {
                const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${e.latlng.lat.toFixed(6)}&longitude=${e.latlng.lng.toFixed(6)}`);
                const j = await r.json();
                setElev(Array.isArray(j.elevation) ? Math.round(j.elevation[0]) : null);
            } catch (err) { setElev(null); }
        });
        map.on("dblclick", (e) => {
            if (!orthoActive) return;
            L.DomEvent.stop(e);
            // Remove o vértice fantasma duplicado do duplo-clique antes de fechar.
            orthoFinish();
        });
        const onKey = (ev) => {
            if (!orthoActive) return;
            if (ev.key === "Escape") activatePan();
            else if (ev.key === "Enter") orthoFinish();
        };
        document.addEventListener("keydown", onKey);

        setTimeout(() => map.invalidateSize(), 200);
        return () => { document.removeEventListener("keydown", onKey); map.remove(); mapObj.current = null; };
    }, []);

    useEffect(() => {
        const el = toolBtns.current && toolBtns.current.locate;
        if (el) el.classList.toggle("geo-locating", locating);
    }, [locating]);

    // Camada permanente "Recursos Minerais" — servida pelo backend já com a codificação correta (acentuação).
    const [permLoading, setPermLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/geo/recurso-mineral`;
        const tryAdd = (attempt = 0) => {
            if (cancelled) return;
            if (!mapObj.current) {
                if (attempt < 40) setTimeout(() => tryAdd(attempt + 1), 150);
                return;
            }
            fetch(url)
                .then((r) => { if (!r.ok) throw new Error("falha ao carregar"); return r.json(); })
                .then((gj) => {
                    if (cancelled || !mapObj.current) return;
                    const feats = (gj.features || []).filter((f) => f.geometry && f.geometry.coordinates);
                    recursoFeaturesRef.current = feats;
                    const counts = {};
                    feats.forEach((f) => { const s = (f.properties && f.properties.subst_prin) || "(sem substância)"; counts[s] = (counts[s] || 0) + 1; });
                    const list = Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
                    setSubstList(list);
                    setSelectedSubs(new Set(list.map((x) => x.name))); // dispara render inicial no efeito de filtro
                })
                .catch(() => { if (!cancelled) toast.error("Não foi possível carregar a camada Recursos Minerais."); })
                .finally(() => { if (!cancelled) setPermLoading(false); });
        };
        tryAdd();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const switchBase = (which) => {
        const map = mapObj.current;
        if (!map || which === basemap) return;
        Object.values(baseLayers.current).forEach((l) => map.removeLayer(l));
        baseLayers.current[which].addTo(map);
        setBasemap(which);
    };

    const addGeoJSON = (gj, name, opts = {}) => {
        const map = mapObj.current;
        if (!gj || (!gj.features && !Array.isArray(gj))) throw new Error("Sem geometria reconhecível");
        const color = opts.color || PALETTE[Object.keys(layerStore.current).length % PALETTE.length];
        const id = opts.id || `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const cluster = L.markerClusterGroup({
            chunkedLoading: true, showCoverageOnHover: false,
            spiderfyOnMaxZoom: true, maxClusterRadius: 55,
        });
        const dotIcon = L.divIcon({
            className: "geo-pt", iconSize: [12, 12], iconAnchor: [6, 6],
            html: `<span style="display:block;width:12px;height:12px;border-radius:50%;background:${color};border:1px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25)"></span>`,
        });
        const gl = L.geoJSON(gj, {
            style: { color, weight: 2, fillColor: color, fillOpacity: 0.25 },
            pointToLayer: (f, latlng) => L.marker(latlng, { icon: dotIcon }),
            onEachFeature: (f, layer) => {
                const props = f.properties || {};
                const keys = Object.keys(props).slice(0, 15);
                if (keys.length) {
                    // Conteúdo lazy: o HTML só é montado quando o popup é aberto (ganho de performance).
                    layer.bindPopup(
                        () => `<div style="max-height:200px;overflow:auto;font-size:12px">${keys.map((k) => `<b>${k}:</b> ${String(props[k] ?? "").slice(0, 80)}`).join("<br/>")}</div>`,
                        { maxWidth: 300 });
                }
                // Rótulo permanente por substância — só aparece quando o ponto se separa do cluster (zoom).
                if (opts.labelField && props[opts.labelField] && layer.bindTooltip) {
                    layer.bindTooltip(String(props[opts.labelField]), {
                        permanent: true, direction: "top", offset: [0, -6],
                        className: "geo-subst-label", opacity: 0.95,
                    });
                }
            },
        });
        cluster.addLayer(gl);
        cluster.addTo(map);
        cluster._geoName = name;
        layerStore.current[id] = cluster;
        const count = (gj.features || []).length || 0;
        setLayers((ls) => [...ls.filter((l) => l.id !== id), { id, name, color, count, visible: true, permanent: !!opts.permanent }]);
        if (opts.fit === false) return;
        try {
            const b = cluster.getBounds();
            if (b.isValid()) map.fitBounds(b, { padding: [30, 30], maxZoom: 16 });
        } catch (e) { /* sem bounds */ }
    };

    // Camada Recursos Minerais: construída a partir das features filtradas por substância, com cor por substância.
    const buildRecursoCluster = (features, selectedSet) => {
        const cluster = L.markerClusterGroup({
            chunkedLoading: true, showCoverageOnHover: false,
            spiderfyOnMaxZoom: true, maxClusterRadius: 55,
            iconCreateFunction: (cl) => {
                const children = cl.getAllChildMarkers();
                const counts = {};
                let top = null, max = 0;
                for (let i = 0; i < children.length; i++) {
                    const key = children[i]._subst || "(sem substância)";
                    const v = (counts[key] = (counts[key] || 0) + 1);
                    if (v > max) { max = v; top = key; }
                }
                const n = cl.getChildCount();
                const bg = substColor(top);
                const fg = hexTextColor(bg);
                const size = n < 10 ? 30 : n < 100 ? 36 : n < 1000 ? 44 : 52;
                return L.divIcon({
                    html: `<div class="geo-cl-inner" style="background:${bg};color:${fg};width:${size}px;height:${size}px;line-height:${size - 4}px" title="Predominante: ${top}">${n}</div>`,
                    className: "geo-cl", iconSize: [size, size],
                });
            },
        });
        const feats = selectedSet
            ? features.filter((f) => selectedSet.has(((f.properties && f.properties.subst_prin) || "(sem substância)")))
            : features;
        const markers = feats.map((f) => {
            const c = f.geometry.coordinates;
            const props = f.properties || {};
            const col = substColor(props.subst_prin);
            const icon = L.divIcon({
                className: "geo-pt", iconSize: [12, 12], iconAnchor: [6, 6],
                html: `<span style="display:block;width:11px;height:11px;border-radius:50%;background:${col};border:1px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25)"></span>`,
            });
            const m = L.marker([c[1], c[0]], { icon });
            m._subst = props.subst_prin || "(sem substância)";
            const keys = Object.keys(props).slice(0, 15);
            m.bindPopup(() => `<div style="max-height:200px;overflow:auto;font-size:12px">${keys.map((k) => `<b>${k}:</b> ${String(props[k] ?? "").slice(0, 80)}`).join("<br/>")}</div>`, { maxWidth: 300 });
            if (props.subst_prin) m.bindTooltip(String(props.subst_prin), { permanent: true, direction: "top", offset: [0, -6], className: "geo-subst-label", opacity: 0.95 });
            return m;
        });
        cluster.addLayers(markers);
        cluster._geoName = "Recursos Minerais";
        return { cluster, count: feats.length };
    };

    const renderRecurso = (features, selectedSet) => {
        const map = mapObj.current;
        if (!map || !features) return;
        const id = "recurso_mineral";
        const { cluster, count } = buildRecursoCluster(features, selectedSet);
        const prev = layerStore.current[id];
        if (prev) map.removeLayer(prev);
        layerStore.current[id] = cluster;
        if (recursoVisibleRef.current) cluster.addTo(map);
        setLayers((ls) => {
            const entry = { id, name: "Recursos Minerais", color: OUTRAS_COLOR, count, visible: recursoVisibleRef.current, permanent: true, multicolor: true };
            return ls.some((l) => l.id === id) ? ls.map((l) => (l.id === id ? { ...l, count, visible: recursoVisibleRef.current } : l)) : [...ls, entry];
        });
        setPermLoading(false);
    };

    const toggleSubst = (name) => setSelectedSubs((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
    const isolateSubst = (name) => setSelectedSubs(new Set([name]));
    const selectAllSubs = () => setSelectedSubs(new Set(substList.map((x) => x.name)));
    const clearSubs = () => setSelectedSubs(new Set());
    const [recExporting, setRecExporting] = useState(null);

    // Exporta apenas as substâncias filtradas no momento (KMZ ou Shapefile), a partir das features carregadas.
    const exportRecurso = async (format) => {
        const feats = recursoFeaturesRef.current;
        if (!feats || !selectedSubs) return;
        const sel = feats.filter((f) => selectedSubs.has(((f.properties && f.properties.subst_prin) || "(sem substância)")));
        if (!sel.length) { toast.error("Nenhuma substância selecionada para exportar."); return; }
        setRecExporting(format);
        try {
            const gj = {
                type: "FeatureCollection",
                features: sel.map((f) => ({ type: "Feature", properties: { ...f.properties, name: f.properties.subst_prin || "" }, geometry: f.geometry })),
            };
            if (format === "kmz") {
                const kml = tokml(gj, { name: "name", documentName: "GM2 Recursos Minerais" });
                const zip = new JSZip();
                zip.file("doc.kml", kml);
                const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
                triggerDownload(blob, `gm2_recursos_minerais_${Date.now()}.kmz`);
                toast.success(`KMZ gerado (${sel.length.toLocaleString("pt-BR")} pontos).`);
            } else {
                const blob = await shpwrite.zip(gj, { outputType: "blob", compression: "DEFLATE", types: { point: "recursos_minerais" } });
                triggerDownload(blob, `gm2_recursos_minerais_${Date.now()}.zip`);
                toast.success(`Shapefile gerado (${sel.length.toLocaleString("pt-BR")} pontos).`);
            }
        } catch (e) {
            toast.error(`Erro ao gerar arquivo: ${e.message || e}`);
        } finally {
            setRecExporting(null);
        }
    };

    // Re-renderiza a camada (debounce) sempre que o filtro de substâncias muda.
    useEffect(() => {
        if (!recursoFeaturesRef.current || !selectedSubs) return;
        const t = setTimeout(() => renderRecurso(recursoFeaturesRef.current, selectedSubs), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubs]);

    // Resumo por substância na área visível (atualiza ao mover/dar zoom).
    const recomputeViewSummary = () => {
        const map = mapObj.current;
        const feats = recursoFeaturesRef.current;
        if (!map || !feats || !selectedSubs) { setViewSummary([]); setViewTotal(0); return; }
        const b = map.getBounds();
        const counts = {};
        let total = 0;
        for (let i = 0; i < feats.length; i++) {
            const f = feats[i];
            const s = (f.properties && f.properties.subst_prin) || "(sem substância)";
            if (!selectedSubs.has(s)) continue;
            const c = f.geometry.coordinates;
            if (b.contains([c[1], c[0]])) { counts[s] = (counts[s] || 0) + 1; total++; }
        }
        const list = Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
        setViewSummary(list);
        setViewTotal(total);
    };
    useEffect(() => {
        const map = mapObj.current;
        if (!map) return;
        const handler = () => recomputeViewSummary();
        map.on("moveend zoomend", handler);
        recomputeViewSummary();
        return () => { map.off("moveend zoomend", handler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubs]);

    // Garante leitura correta de acentos: injeta .cpg (windows-1252) se o ZIP não trouxer um.
    const ensureCpg = async (arrayBuffer) => {
        try {
            const zip = await JSZip.loadAsync(arrayBuffer);
            const names = Object.keys(zip.files);
            if (names.some((n) => n.toLowerCase().endsWith(".cpg"))) return arrayBuffer;
            const shpNames = names.filter((n) => n.toLowerCase().endsWith(".shp"));
            if (!shpNames.length) return arrayBuffer;
            shpNames.forEach((n) => zip.file(n.slice(0, -4) + ".cpg", "windows-1252"));
            return await zip.generateAsync({ type: "arraybuffer" });
        } catch (e) { return arrayBuffer; }
    };

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setBusy(true);
        for (const file of files) {
            const ext = (file.name.split(".").pop() || "").toLowerCase();
            try {
                if (ext === "kml") {
                    const dom = new DOMParser().parseFromString(await file.text(), "text/xml");
                    addGeoJSON(toGeoJSON.kml(dom), file.name);
                } else if (ext === "kmz") {
                    const zip = await JSZip.loadAsync(await file.arrayBuffer());
                    const kmlEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith(".kml"));
                    if (!kmlEntry) throw new Error("KMZ sem .kml interno");
                    const dom = new DOMParser().parseFromString(await kmlEntry.async("text"), "text/xml");
                    addGeoJSON(toGeoJSON.kml(dom), file.name);
                } else if (ext === "zip") {
                    const gj = await shp(await ensureCpg(await file.arrayBuffer()));
                    const arr = Array.isArray(gj) ? gj : [gj];
                    arr.forEach((fc, i) => addGeoJSON(fc, arr.length > 1 ? `${file.name} · ${fc.fileName || i + 1}` : file.name));
                } else {
                    throw new Error("Use .kml, .kmz ou .zip (shapefile)");
                }
                toast.success(`"${file.name}" carregado no mapa.`);
            } catch (e) {
                toast.error(`Erro em "${file.name}": ${e.message || e}`);
            }
        }
        setBusy(false);
        if (fileInput.current) fileInput.current.value = "";
    };

    const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

    const toggleLayer = (id) => {
        const gl = layerStore.current[id];
        if (!gl || !mapObj.current) return;
        setLayers((ls) => ls.map((l) => {
            if (l.id !== id) return l;
            const vis = !l.visible;
            if (vis) mapObj.current.addLayer(gl); else mapObj.current.removeLayer(gl);
            if (id === "recurso_mineral") recursoVisibleRef.current = vis;
            return { ...l, visible: vis };
        }));
    };
    const removeLayer = (id) => {
        const gl = layerStore.current[id];
        if (gl && mapObj.current) mapObj.current.removeLayer(gl);
        delete layerStore.current[id];
        setLayers((ls) => ls.filter((l) => l.id !== id));
    };
    const clearAll = () => {
        Object.entries(layerStore.current).forEach(([id, gl]) => {
            if (layers.find((l) => l.id === id)?.permanent) return;
            if (mapObj.current) mapObj.current.removeLayer(gl);
            delete layerStore.current[id];
        });
        setLayers((ls) => ls.filter((l) => l.permanent));
        if (drawnRef.current) drawnRef.current.clearLayers();
        setMeasures([]);
        setDrawCount(0);
    };
    const zoomTo = (id) => {
        const gl = layerStore.current[id];
        try {
            const b = gl.getBounds();
            if (b.isValid()) mapObj.current.fitBounds(b, { padding: [30, 30], maxZoom: 16 });
        } catch (e) { /* noop */ }
    };

    const fullExtent = () => {
        const map = mapObj.current;
        if (!map) return;
        const groups = [...Object.values(layerStore.current), drawnRef.current].filter(Boolean);
        let bounds = null;
        groups.forEach((g) => {
            try {
                const b = g.getBounds();
                if (b && b.isValid()) bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
            } catch (e) { /* noop */ }
        });
        if (bounds && bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
        else map.setView([-14.2, -51.9], 4);
    };

    const drawnGeoJSON = () => {
        const drawn = drawnRef.current;
        if (!drawn || !drawn.getLayers().length) return null;
        return drawn.toGeoJSON();
    };
    const triggerDownload = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    };
    const geomKind = (g) => {
        const t = (g && g.type) || "";
        if (t.includes("Point")) return "point";
        if (t.includes("LineString")) return "line";
        if (t.includes("Polygon")) return "polygon";
        return "";
    };
    const drawnTypes = () => {
        const gj = drawnGeoJSON();
        if (!gj) return [];
        const s = new Set();
        (gj.features || []).forEach((f) => { const k = geomKind(f.geometry); if (k) s.add(k); });
        return [...s];
    };
    const filteredGeoJSON = (type) => {
        const gj = drawnGeoJSON();
        if (!gj) return null;
        if (type === "todos") return gj;
        return { type: "FeatureCollection", features: (gj.features || []).filter((f) => geomKind(f.geometry) === type) };
    };
    const askExport = (format) => {
        if (!drawCount) { toast.error("Desenhe ao menos um ponto, linha ou polígono."); return; }
        setExportAsk({ format });
    };
    const doExport = async (type) => {
        const format = exportAsk && exportAsk.format;
        setExportAsk(null);
        const gj = filteredGeoJSON(type);
        if (!gj || !(gj.features || []).length) { toast.error("Nenhuma geometria desse tipo para baixar."); return; }
        const suffix = type === "todos" ? "todos" : type;
        if (format === "kmz") {
            try {
                const kml = tokml(gj, { name: "name", documentName: "GM2 Desenho" });
                const zip = new JSZip();
                zip.file("doc.kml", kml);
                const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
                triggerDownload(blob, `gm2_${suffix}_${Date.now()}.kmz`);
                toast.success("KMZ gerado.");
            } catch (e) { toast.error(`Erro ao gerar KMZ: ${e.message || e}`); }
        } else {
            try {
                const blob = await shpwrite.zip(gj, { outputType: "blob", compression: "DEFLATE",
                    types: { point: "pontos", polygon: "poligonos", polyline: "linhas", line: "linhas" } });
                triggerDownload(blob, `gm2_${suffix}_${Date.now()}.zip`);
                toast.success("Shapefile (.zip) gerado.");
            } catch (e) { toast.error(`Erro ao gerar Shapefile: ${e.message || e}`); }
        }
    };

    const dms = (deg, isLat) => toDMS(deg, isLat);

    const copySummary = async (rows) => {
        const text = (rows || []).map(([k, v]) => `${k}: ${v}`).join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Resumo copiado.");
        } catch (e) {
            toast.error("Não foi possível copiar.");
        }
    };
    const copyVertices = async (vertices) => {
        const text = (vertices || []).map((v) => `${v.i}\t${v.dms}\t${v.dec}`).join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Vértices copiados (DMS + decimal).");
        } catch (e) {
            toast.error("Não foi possível copiar.");
        }
    };

    // ---- ANM / REPEM ----
    const ringOfDrawn = (id) => {
        const drawn = drawnRef.current;
        if (!drawn) return null;
        let found = null;
        drawn.eachLayer((l) => { if (L.Util.stamp(l) === id && l instanceof L.Polygon) found = l; });
        if (!found) return null;
        const lls = found.getLatLngs();
        const ring = Array.isArray(lls[0]) ? lls[0] : lls;
        return ring.map((p) => ({ lat: p.lat, lng: p.lng }));
    };
    const openAnm = (id) => {
        const ring = ringOfDrawn(id);
        if (!ring || ring.length < 3) { toast.error("Selecione um polígono válido."); return; }
        setAnm({ id, ring, identifier: "AREA_001", validation: validateAnm(ring), preview: buildVertices(ring), adjust: null });
    };
    const sanitizeId = (s) => ((s || "AREA_001").trim().toUpperCase().replace(/[^A-Z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "AREA_001");
    const downloadRepemCSV = () => {
        if (!anm) return;
        const blob = new Blob(["\ufeff" + repemCSV(anm.ring)], { type: "text/csv;charset=utf-8" });
        triggerDownload(blob, `COORDENADAS_ANM_REPEM_${sanitizeId(anm.identifier)}.csv`);
        toast.success("CSV ANM/REPEM gerado.");
    };
    const previewAdjust = () => {
        if (!anm) return;
        const { adjusted, changes } = adjustRepem(anm.ring);
        if (!changes.length) { toast.info("A poligonal já está no padrão REPEM (rumos verdadeiros)."); return; }
        setAnm((a) => ({ ...a, adjust: { adjusted, changes } }));
    };
    const applyAdjust = () => {
        if (!anm || !anm.adjust) return;
        const ring = anm.adjust.adjusted;
        const drawn = drawnRef.current;
        let layer = null;
        drawn && drawn.eachLayer((l) => { if (L.Util.stamp(l) === anm.id) layer = l; });
        if (layer) { layer.setLatLngs(ring.map((p) => L.latLng(p.lat, p.lng))); if (recomputeRef.current) recomputeRef.current(); }
        setAnm({ id: anm.id, ring, identifier: anm.identifier, validation: validateAnm(ring), preview: buildVertices(ring), adjust: null });
        toast.success("Ajuste REPEM aplicado à poligonal.");
    };
    const downloadPackage = async () => {
        if (!anm) return;
        const id = sanitizeId(anm.identifier);
        try {
            const gj = { type: "FeatureCollection", features: [{ type: "Feature", properties: { id },
                geometry: { type: "Polygon", coordinates: [[...anm.ring, anm.ring[0]].map((p) => [p.lng, p.lat])] } }] };
            const zip = new JSZip();
            const folder = zip.folder(id);
            folder.file(`${id}.geojson`, JSON.stringify(gj, null, 2));
            folder.file(`${id}.kml`, tokml(gj, { name: "id", documentName: id }));
            folder.file(`COORDENADAS_ANM_REPEM_${id}.csv`, "\ufeff" + repemCSV(anm.ring));
            try {
                const shpBlob = await shpwrite.zip(gj, { outputType: "blob", compression: "DEFLATE", types: { polygon: id } });
                const inner = await JSZip.loadAsync(await shpBlob.arrayBuffer());
                await Promise.all(Object.values(inner.files).filter((f) => !f.dir).map(async (f) => {
                    const ext = (f.name.split(".").pop() || "").toLowerCase();
                    if (["shp", "shx", "dbf", "prj"].includes(ext)) folder.file(`${id}.${ext}`, await f.async("uint8array"));
                }));
            } catch (err) { /* shapefile opcional */ }
            const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
            triggerDownload(blob, `${id}.zip`);
            toast.success("Pacote completo gerado.");
        } catch (e) { toast.error(`Erro ao gerar pacote: ${e.message || e}`); }
    };

    const addPointFromCoords = (lat, lng) => {
        const map = mapObj.current, drawn = drawnRef.current;
        if (!map || !drawn) return;
        const marker = L.marker([lat, lng], { draggable: true });
        drawn.addLayer(marker);
        marker.on("pm:edit pm:markerdragend pm:dragend pm:update dragend", () => { if (recomputeRef.current) recomputeRef.current(); });
        marker.on("pm:remove", () => { if (recomputeRef.current) recomputeRef.current(); setDrawCount(drawn.getLayers().length); });
        if (recomputeRef.current) recomputeRef.current();
        setDrawCount(drawn.getLayers().length);
        marker.bindPopup(
            `<div style="font-size:12px"><b>Ponto por coordenadas</b><br>` +
            `Lat: ${lat.toFixed(6)}<br>Long: ${lng.toFixed(6)}<br>` +
            `<span style="color:#64748b">${toDMS(lat, true)} ${toDMS(lng, false)}</span></div>`
        ).openPopup();
        map.setView([lat, lng], Math.max(map.getZoom(), 15), { animate: true });
    };

    const submitCoordPoint = () => {
        setCoordErr("");
        let lat, lng;
        if (coordMode === "decimal") {
            lat = parseFloat(String(decForm.lat).replace(",", "."));
            lng = parseFloat(String(decForm.lng).replace(",", "."));
            if (isNaN(lat) || isNaN(lng)) { setCoordErr("Informe latitude e longitude em número decimal (ex.: -19.923456)."); return; }
        } else {
            const num = (v) => parseFloat(String(v).replace(",", "."));
            const latD = num(dmsForm.latD), latM = num(dmsForm.latM), latS = num(dmsForm.latS);
            const lngD = num(dmsForm.lngD), lngM = num(dmsForm.lngM), lngS = num(dmsForm.lngS);
            if ([latD, latM, latS, lngD, lngM, lngS].some((x) => isNaN(x))) { setCoordErr("Preencha graus, minutos e segundos de latitude e longitude."); return; }
            if (latM < 0 || latM >= 60 || lngM < 0 || lngM >= 60 || latS < 0 || latS >= 60 || lngS < 0 || lngS >= 60) { setCoordErr("Minutos e segundos devem estar entre 0 e 59,99."); return; }
            lat = (Math.abs(latD) + latM / 60 + latS / 3600) * (dmsForm.latH === "S" ? -1 : 1);
            lng = (Math.abs(lngD) + lngM / 60 + lngS / 3600) * (dmsForm.lngH === "O" ? -1 : 1);
        }
        if (lat < -90 || lat > 90) { setCoordErr("Latitude deve estar entre -90 e 90."); return; }
        if (lng < -180 || lng > 180) { setCoordErr("Longitude deve estar entre -180 e 180."); return; }
        addPointFromCoords(lat, lng);
        setCoordModal(false);
        setDecForm({ lat: "", lng: "" });
        setDmsForm({ latD: "", latM: "", latS: "", latH: "N", lngD: "", lngM: "", lngS: "", lngH: "O" });
        toast.success("Ponto adicionado ao mapa.");
    };

    return (
        <div className="space-y-4" data-testid="geo-page">
            {coordModal && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 px-4" data-testid="geo-coord-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-mineral" />
                            <h3 className="font-heading text-lg font-black text-slate-900">Adicionar ponto por coordenadas</h3>
                            <button onClick={() => setCoordModal(false)} data-testid="geo-coord-close" className="ml-auto text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="mt-4 inline-flex rounded-sm border border-slate-200 overflow-hidden text-sm">
                            <button onClick={() => setCoordMode("decimal")} data-testid="geo-coord-mode-decimal"
                                className={`px-4 py-2 font-medium transition-colors ${coordMode === "decimal" ? "bg-petroleum text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Decimal</button>
                            <button onClick={() => setCoordMode("dms")} data-testid="geo-coord-mode-dms"
                                className={`px-4 py-2 font-medium transition-colors ${coordMode === "dms" ? "bg-petroleum text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Graus, min, seg</button>
                        </div>

                        {coordMode === "decimal" ? (
                            <div className="mt-4 space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Latitude</label>
                                    <input value={decForm.lat} onChange={(e) => setDecForm({ ...decForm, lat: e.target.value })} data-testid="geo-dec-lat"
                                        placeholder="Ex.: -19.923456" className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Longitude</label>
                                    <input value={decForm.lng} onChange={(e) => setDecForm({ ...decForm, lng: e.target.value })} data-testid="geo-dec-lng"
                                        placeholder="Ex.: -43.945678" className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30 focus:border-mineral" />
                                </div>
                                <p className="text-xs text-slate-400">Use ponto ou vírgula. Sul e Oeste são negativos (ex.: -19.92, -43.94).</p>
                            </div>
                        ) : (
                            <div className="mt-4 space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Latitude</label>
                                    <div className="mt-1 grid grid-cols-4 gap-2">
                                        <input value={dmsForm.latD} onChange={(e) => setDmsForm({ ...dmsForm, latD: e.target.value })} data-testid="geo-dms-lat-d" placeholder="Graus" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <input value={dmsForm.latM} onChange={(e) => setDmsForm({ ...dmsForm, latM: e.target.value })} data-testid="geo-dms-lat-m" placeholder="Min" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <input value={dmsForm.latS} onChange={(e) => setDmsForm({ ...dmsForm, latS: e.target.value })} data-testid="geo-dms-lat-s" placeholder="Seg" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <select value={dmsForm.latH} onChange={(e) => setDmsForm({ ...dmsForm, latH: e.target.value })} data-testid="geo-dms-lat-h" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-mineral/30">
                                            <option value="N">N</option><option value="S">S</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Longitude</label>
                                    <div className="mt-1 grid grid-cols-4 gap-2">
                                        <input value={dmsForm.lngD} onChange={(e) => setDmsForm({ ...dmsForm, lngD: e.target.value })} data-testid="geo-dms-lng-d" placeholder="Graus" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <input value={dmsForm.lngM} onChange={(e) => setDmsForm({ ...dmsForm, lngM: e.target.value })} data-testid="geo-dms-lng-m" placeholder="Min" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <input value={dmsForm.lngS} onChange={(e) => setDmsForm({ ...dmsForm, lngS: e.target.value })} data-testid="geo-dms-lng-s" placeholder="Seg" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-mineral/30" />
                                        <select value={dmsForm.lngH} onChange={(e) => setDmsForm({ ...dmsForm, lngH: e.target.value })} data-testid="geo-dms-lng-h" className="px-2 py-2.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-mineral/30">
                                            <option value="L">L (Leste)</option><option value="O">O (Oeste)</option>
                                        </select>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400">Segundos aceitam até 2 casas decimais (ex.: 24,35). No Brasil use hemisférios S e O.</p>
                            </div>
                        )}

                        {coordErr && <p className="mt-3 text-sm text-red-600" data-testid="geo-coord-error">{coordErr}</p>}

                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setCoordModal(false)} data-testid="geo-coord-cancel" className="flex-1 py-2.5 text-sm font-semibold rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</button>
                            <button onClick={submitCoordPoint} data-testid="geo-coord-submit" className="flex-1 py-2.5 text-sm font-bold rounded-sm bg-mineral hover:bg-mineralHover text-white inline-flex items-center justify-center gap-2"><MapPin className="w-4 h-4" />Adicionar ponto</button>
                        </div>
                    </div>
                </div>
            )}
            <div>
                <h1 className="font-heading font-black text-2xl text-slate-800">Inteligência Geoespacial</h1>
                <p className="text-sm text-slate-500 mt-1 max-w-3xl">Visualize e desenhe suas áreas no mapa. A camada <b>Recursos Minerais</b> já vem carregada e fixa (ligue/desligue no painel <b>Camadas</b>); ao dar zoom, cada ocorrência é <b>rotulada pela substância</b>, com <b>cores por substância</b> e um <b>filtro</b> para exibir só o que interessa (painel <b>Substâncias</b>). Carregue <b>KML</b>, <b>KMZ</b> ou <b>Shapefile (.zip)</b>, use as ferramentas de <b>desenho</b> (ponto, linha e polígono) e baixe o que desenhar em <b>KMZ</b> ou <b>Shapefile</b>. Alterne entre Mapa e Satélite.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-3 shadow-sm flex flex-wrap items-center gap-2">
                <input ref={fileInput} type="file" accept=".kml,.kmz,.zip" multiple className="hidden" data-testid="geo-upload-input" onChange={(e) => handleFiles(e.target.files)} />
                <button onClick={() => fileInput.current && fileInput.current.click()} disabled={busy} data-testid="geo-upload-btn"
                    className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 transition-colors">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Carregar arquivos
                </button>
                <span className="text-xs text-slate-400 hidden sm:inline">KML · KMZ · Shapefile (.zip)</span>

                <div className="ml-auto inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid="geo-base-toggle">
                    <button onClick={() => switchBase("mapa")} data-testid="geo-base-mapa"
                        className={`px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${basemap === "mapa" ? "bg-petroleum text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                        <MapIcon className="w-4 h-4" />Mapa
                    </button>
                    <button onClick={() => switchBase("satelite")} data-testid="geo-base-satelite"
                        className={`px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${basemap === "satelite" ? "bg-petroleum text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                        <Satellite className="w-4 h-4" />Satélite
                    </button>
                </div>
                <button onClick={clearAll} disabled={!layers.length && !drawCount} data-testid="geo-clear"
                    className="border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-sm px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                    <Trash2 className="w-4 h-4" />Limpar
                </button>
                <button onClick={fullExtent} data-testid="geo-full-extent" title="Enquadrar todas as camadas / extensão total"
                    className="border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-sm px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors">
                    <Maximize className="w-4 h-4" />Extensão
                </button>
                <button onClick={() => { setCoordErr(""); setCoordModal(true); }} data-testid="geo-add-coord-btn" title="Adicionar ponto digitando as coordenadas (decimal ou GMS)"
                    className="border border-mineral text-mineralHover hover:bg-mineral/10 rounded-sm px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors">
                    <MapPin className="w-4 h-4" />Ponto por coordenada
                </button>
                <div className="w-px h-6 bg-slate-200 mx-0.5" />
                <span className="text-xs text-slate-500 inline-flex items-center gap-1"><PenLine className="w-3.5 h-3.5 text-mineral" />Desenho:</span>
                <button onClick={() => askExport("kmz")} disabled={!drawCount} data-testid="geo-download-kmz" title="Baixar o que você desenhou em KMZ"
                    className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                    <Download className="w-4 h-4" />KMZ
                </button>
                <button onClick={() => askExport("shp")} disabled={!drawCount} data-testid="geo-download-shp" title="Baixar o que você desenhou em Shapefile (.zip)"
                    className="bg-slate-700 hover:bg-slate-800 text-white rounded-sm px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                    <Download className="w-4 h-4" />Shapefile
                </button>
            </div>

            <div className="relative rounded-md overflow-hidden border border-slate-200 shadow-sm" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
                <div ref={mapRef} data-testid="geo-map" className="w-full h-[calc(100vh-300px)] min-h-[520px] bg-slate-100 z-0" />

                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none" data-testid="geo-coords">
                    <div className="bg-darkblue/90 text-sky-200 font-mono text-xs sm:text-sm px-3 py-1 rounded shadow-md border border-white/10 whitespace-nowrap">
                        {coord ? `${dms(coord.lat, true)}  ${dms(coord.lng, false)}` : "mova o cursor sobre o mapa"}
                        <span className="text-slate-400"> elev </span>
                        <span className="text-white">{elev === "…" ? "…" : (elev === null || elev === undefined ? "—" : `${elev} m`)}</span>
                    </div>
                </div>

                <div className="absolute top-3 right-3 z-[1000] w-64 max-w-[70%] flex flex-col gap-2 max-h-[calc(100vh-1.5rem)]">
                    {substList.length > 0 && (
                        <div className="bg-white/95 backdrop-blur rounded-md shadow-md border border-slate-200 flex flex-col min-h-0" data-testid="geo-viewsummary-panel">
                            <button onClick={() => setViewSumOpen((o) => !o)} data-testid="geo-viewsummary-collapse"
                                className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-slate-700 font-semibold text-sm">
                                <BarChart3 className="w-4 h-4 text-mineral" />Resumo na área
                                <span className="ml-auto text-xs font-normal text-slate-400" title="Total de ocorrências visíveis" data-testid="geo-viewsummary-total">{viewTotal.toLocaleString("pt-BR")}</span>
                                {viewSumOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                            </button>
                            {viewSumOpen && (
                                <div className="p-2 max-h-[34vh] overflow-y-auto" data-testid="geo-viewsummary-list">
                                    {viewSummary.length === 0 ? (
                                        <p className="text-xs text-slate-400 px-1 py-1">Nenhuma ocorrência na área visível. Aproxime ou desloque o mapa.</p>
                                    ) : viewSummary.map((s) => (
                                        <button key={s.name} onClick={() => isolateSubst(s.name)} title={`Ver só ${s.name}`} data-testid={`geo-viewsummary-row-${s.name}`}
                                            className="w-full flex items-center gap-2 text-xs px-1 py-0.5 rounded hover:bg-slate-50">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: substColor(s.name) }} />
                                            <span className="flex-1 truncate text-left text-slate-700" title={s.name}>{s.name}</span>
                                            <span className="text-slate-400">{s.count.toLocaleString("pt-BR")}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="bg-white/95 backdrop-blur rounded-md shadow-md border border-slate-200" data-testid="geo-measure-panel">
                        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-slate-700 font-semibold text-sm">
                            <PenLine className="w-4 h-4 text-mineral" />Medição
                        </div>
                        <div className="p-2 space-y-1 max-h-[40vh] overflow-y-auto">
                            {live ? (
                                <div className="text-xs bg-mineral/10 text-mineral rounded-sm px-2 py-1.5 font-medium" data-testid="geo-measure-live">{live.text}</div>
                            ) : measures.length === 0 ? (
                                <p className="text-xs text-slate-400 px-1 py-1">Desenhe uma linha ou polígono para ver comprimento e área (ha) ao vivo.</p>
                            ) : (
                                measures.map((m) => (
                                    <div key={m.id} className="bg-slate-50 rounded-md border border-slate-100 p-2" data-testid="geo-measure-item">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: m.color || "#334155" }} />
                                            <span className="text-xs font-bold text-slate-700 capitalize">{m.type === "polygon" ? "Polígono" : m.type === "line" ? "Linha" : "Ponto"}</span>
                                            <button onClick={() => copySummary(m.rows)} title="Copiar resumo" data-testid="geo-measure-copy"
                                                className="ml-auto text-slate-400 hover:text-petroleum transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                                            {m.type === "polygon" && (
                                                <button onClick={() => openAnm(m.id)} title="Gerar CSV ANM/REPEM" data-testid="geo-anm-open"
                                                    className="text-mineral hover:text-mineralHover transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /></button>
                                            )}
                                        </div>
                                        <table className="w-full text-[11px]">
                                            <tbody>
                                                {(m.rows || []).filter(([k]) => k !== "Tipo").map(([k, v]) => (
                                                    <tr key={k}>
                                                        <td className="text-slate-400 pr-2 align-top whitespace-nowrap">{k}</td>
                                                        <td className="text-slate-700 font-medium break-all">{v}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {m.vertices && m.vertices.length >= 2 && (
                                            <details className="mt-1.5" data-testid="geo-measure-vertices">
                                                <summary className="text-[11px] font-semibold text-petroleum cursor-pointer select-none flex items-center gap-1">
                                                    Vértices (lat/long DMS)
                                                    <button type="button" onClick={(e) => { e.preventDefault(); copyVertices(m.vertices); }}
                                                        title="Copiar todos os vértices" data-testid="geo-measure-vertices-copy"
                                                        className="ml-auto text-slate-400 hover:text-petroleum transition-colors"><Copy className="w-3 h-3" /></button>
                                                </summary>
                                                <ol className="mt-1 space-y-0.5">
                                                    {m.vertices.map((v) => (
                                                        <li key={v.i} className="text-[10px] text-slate-600 flex gap-1.5 leading-tight">
                                                            <span className="text-slate-400 w-4 shrink-0 text-right">{v.i}</span>
                                                            <span className="font-mono break-all">{v.dms}</span>
                                                        </li>
                                                    ))}
                                                </ol>
                                            </details>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-3 left-16 z-[1000] w-60 max-w-[60%] flex flex-col gap-2 max-h-[calc(100vh-7rem)]">
                    <div className="bg-white/95 backdrop-blur rounded-md shadow-md border border-slate-200">
                        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-slate-700 font-semibold text-sm">
                            <Layers className="w-4 h-4 text-mineral" />Camadas <span className="ml-auto text-xs font-normal text-slate-400">{layers.length}</span>
                        </div>
                        <div className="max-h-[26vh] overflow-y-auto p-2 space-y-1" data-testid="geo-layer-list">
                            {permLoading && <p className="text-xs text-slate-400 px-1 py-2 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Carregando camada Recursos Minerais…</p>}
                            {!permLoading && layers.length === 0 && <p className="text-xs text-slate-400 px-1 py-2">Nenhuma camada. Carregue ou arraste um arquivo para o mapa.</p>}
                            {layers.map((l) => (
                                <div key={l.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-sm px-2 py-1.5" data-testid="geo-layer-item">
                                    <input type="checkbox" checked={l.visible !== false} onChange={() => toggleLayer(l.id)}
                                        title={l.visible !== false ? "Desligar camada" : "Ligar camada"} data-testid={`geo-layer-toggle-${l.id}`}
                                        className="accent-mineral w-3.5 h-3.5 shrink-0 cursor-pointer" />
                                    <span className="w-3 h-3 rounded-sm shrink-0" style={l.multicolor ? { background: "conic-gradient(#f59f00,#e03131,#22b8cf,#7048e8,#2f9e44,#f59f00)" } : { backgroundColor: l.color }} />
                                    <span className="truncate flex-1 text-slate-700" title={l.name}>{l.name}{l.permanent && <span className="ml-1 text-[9px] font-bold text-petroleum/70 uppercase">fixa</span>}</span>
                                    <span className="text-slate-400">{l.count}</span>
                                    <button onClick={() => zoomTo(l.id)} title="Ir para" className="text-slate-400 hover:text-petroleum"><Crosshair className="w-3.5 h-3.5" /></button>
                                    {!l.permanent && <button onClick={() => removeLayer(l.id)} title="Remover" data-testid={`geo-layer-remove-${l.id}`} className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {substList.length > 0 && (
                        <div className="bg-white/95 backdrop-blur rounded-md shadow-md border border-slate-200 flex flex-col min-h-0" data-testid="geo-subst-panel">
                            <button onClick={() => setSubstOpen((o) => !o)} data-testid="geo-subst-collapse"
                                className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-slate-700 font-semibold text-sm">
                                <Gem className="w-4 h-4 text-mineral" />Substâncias
                                <span className="ml-auto text-xs font-normal text-slate-400">{selectedSubs ? selectedSubs.size : 0}/{substList.length}</span>
                                {substOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                            </button>
                            {substOpen && (
                                <div className="p-2 space-y-2 flex flex-col min-h-0">
                                    <div className="flex flex-wrap gap-1" data-testid="geo-subst-legend">
                                        {substList.filter((s) => SUBST_COLORS[s.name.toUpperCase()]).slice(0, 12).map((s) => (
                                            <button key={s.name} onClick={() => isolateSubst(s.name)} title={`Ver só ${s.name} (${s.count.toLocaleString("pt-BR")})`}
                                                data-testid={`geo-subst-legend-${s.name}`}
                                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-600">
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: substColor(s.name) }} />{s.name}
                                            </button>
                                        ))}
                                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500">
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: OUTRAS_COLOR }} />Outras
                                        </span>
                                    </div>
                                    <input value={substSearch} onChange={(e) => setSubstSearch(e.target.value)} placeholder="Buscar substância..." data-testid="geo-subst-search"
                                        className="w-full text-xs border border-slate-200 rounded px-2 py-1 outline-none focus:border-mineral" />
                                    <div className="flex items-center gap-3 text-[11px]">
                                        <button onClick={selectAllSubs} data-testid="geo-subst-all" className="text-petroleum hover:underline">Selecionar todas</button>
                                        <button onClick={clearSubs} data-testid="geo-subst-none" className="text-slate-500 hover:underline">Limpar</button>
                                    </div>
                                    <div className="overflow-y-auto pr-1 space-y-0.5 max-h-[32vh]" data-testid="geo-subst-list">
                                        {substList.filter((s) => s.name.toLowerCase().includes(substSearch.toLowerCase())).map((s) => (
                                            <label key={s.name} data-testid={`geo-subst-row-${s.name}`}
                                                className="flex items-center gap-2 text-xs px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer">
                                                <input type="checkbox" checked={!!selectedSubs && selectedSubs.has(s.name)} onChange={() => toggleSubst(s.name)}
                                                    className="accent-mineral w-3.5 h-3.5 shrink-0" />
                                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: substColor(s.name) }} />
                                                <span className="flex-1 truncate text-slate-700" title={s.name}>{s.name}</span>
                                                <span className="text-slate-400">{s.count.toLocaleString("pt-BR")}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                                        <span className="text-[11px] text-slate-500 shrink-0">Exportar seleção:</span>
                                        <button onClick={() => exportRecurso("kmz")} disabled={!!recExporting} data-testid="geo-subst-export-kmz"
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-petroleum/30 text-petroleum hover:bg-petroleum/5 disabled:opacity-50">
                                            {recExporting === "kmz" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}KMZ
                                        </button>
                                        <button onClick={() => exportRecurso("shp")} disabled={!!recExporting} data-testid="geo-subst-export-shp"
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-petroleum/30 text-petroleum hover:bg-petroleum/5 disabled:opacity-50">
                                            {recExporting === "shp" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}Shapefile
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <p className="text-[11px] text-slate-400">Camadas base: OpenStreetMap (Mapa) e Esri World Imagery (Satélite). A camada <b>Recursos Minerais</b> é servida pelo GM² com a acentuação corrigida (leitura em Windows-1252/latin-1, evitando caracteres “�”). Seus uploads e desenhos são processados localmente no navegador; ao enviar um Shapefile sem arquivo <code>.cpg</code>, a acentuação é interpretada automaticamente como Windows-1252 (padrão de dados brasileiros ANM/CPRM).</p>

            {exportAsk && (
                <div className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4" data-testid="geo-export-modal" onClick={() => setExportAsk(null)}>
                    <div className="bg-white rounded-md shadow-xl border border-slate-200 w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-1">
                            <Download className="w-4 h-4 text-mineral" />
                            <h3 className="font-heading font-bold text-slate-800">Baixar em {exportAsk.format === "kmz" ? "KMZ" : "Shapefile"}</h3>
                        </div>
                        <p className="text-sm text-slate-500 mb-4">Qual desenho você quer baixar?</p>
                        <div className="space-y-2">
                            {[["point", "Pontos"], ["line", "Linhas"], ["polygon", "Polígonos"]].filter(([k]) => drawnTypes().includes(k)).map(([k, label]) => (
                                <button key={k} onClick={() => doExport(k)} data-testid={`geo-export-${k}`}
                                    className="w-full text-left border border-slate-200 hover:border-mineral hover:bg-mineral/5 rounded-sm px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors">{label}</button>
                            ))}
                            <button onClick={() => doExport("todos")} data-testid="geo-export-todos"
                                className="w-full text-left bg-slate-700 hover:bg-slate-800 text-white rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors">Todos</button>
                        </div>
                        <button onClick={() => setExportAsk(null)} className="mt-4 text-sm text-slate-500 hover:text-slate-700">Cancelar</button>
                    </div>
                </div>
            )}

            {anm && (
                <div className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4" data-testid="geo-anm-modal" onClick={() => setAnm(null)}>
                    <div className="bg-white rounded-md shadow-xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-1">
                            <FileSpreadsheet className="w-5 h-5 text-mineral" />
                            <h3 className="font-heading font-bold text-slate-800 text-lg">Coordenadas ANM/REPEM</h3>
                            <button onClick={() => setAnm(null)} className="ml-auto text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-xs font-semibold text-petroleum mb-3">Datum: SIRGAS 2000 — EPSG:4674</p>

                        <div className="flex items-center gap-2 mb-3">
                            <label className="text-sm text-slate-600">Identificador da área</label>
                            <input data-testid="geo-anm-identifier" value={anm.identifier}
                                onChange={(e) => setAnm((a) => ({ ...a, identifier: e.target.value }))}
                                className="border border-slate-200 rounded-sm px-3 py-1.5 text-sm flex-1" placeholder="AREA_001" />
                        </div>

                        {/* Status de validação */}
                        <div className="rounded-md border border-slate-200 p-3 mb-3" data-testid="geo-anm-status">
                            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700">
                                <ShieldCheck className="w-4 h-4 text-petroleum" />Compatibilidade ANM/REPEM
                            </div>
                            {!anm.validation.ok && (
                                <p className="text-xs text-red-600 mb-2 flex items-start gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Poligonal ainda não está compatível com o padrão ANM/REPEM.
                                </p>
                            )}
                            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
                                {anm.validation.checks.map((c) => (
                                    <li key={c.key} className="text-xs flex items-center gap-1.5" data-testid={`geo-anm-check-${c.key}`}>
                                        {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                                        <span className={c.ok ? "text-slate-600" : "text-red-600 font-medium"}>{c.label}{c.detail ? ` — ${c.detail}` : ""}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Ajuste REPEM (rumos verdadeiros) */}
                        {!anm.validation.checks.find((c) => c.key === "rumos").ok && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-3">
                                <p className="text-xs text-amber-800 flex items-start gap-1 mb-2">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    Atenção: há segmento(s) que não atendem ao padrão de rumos verdadeiros Norte-Sul ou Leste-Oeste utilizado pela ANM/REPEM.
                                </p>
                                {!anm.adjust ? (
                                    <button onClick={previewAdjust} data-testid="geo-anm-adjust-preview"
                                        className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm px-3 py-1.5 text-xs font-semibold">Ajustar para padrão REPEM</button>
                                ) : (
                                    <div>
                                        <p className="text-xs font-semibold text-slate-700 mb-1">Ajustes propostos (não aplicados):</p>
                                        <ul className="text-[11px] text-slate-600 space-y-0.5 max-h-32 overflow-y-auto mb-2">
                                            {anm.adjust.changes.map((ch, i) => (
                                                <li key={i}>V{ch.vertice} · {ch.campo}: <span className="line-through text-slate-400">{ch.de}</span> → <span className="font-semibold text-slate-800">{ch.para}</span></li>
                                            ))}
                                        </ul>
                                        <div className="flex gap-2">
                                            <button onClick={applyAdjust} data-testid="geo-anm-adjust-apply"
                                                className="bg-green-600 hover:bg-green-700 text-white rounded-sm px-3 py-1.5 text-xs font-semibold">Aplicar ajuste</button>
                                            <button onClick={() => setAnm((a) => ({ ...a, adjust: null }))} data-testid="geo-anm-adjust-keep"
                                                className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm px-3 py-1.5 text-xs font-semibold">Manter coordenada original</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pré-visualização das coordenadas */}
                        <div className="rounded-md border border-slate-200 overflow-hidden mb-4">
                            <table className="w-full text-xs" data-testid="geo-anm-preview">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr><th className="text-left px-3 py-1.5 font-semibold">Vértice</th>
                                        <th className="text-left px-3 py-1.5 font-semibold">Latitude SIRGAS2000</th>
                                        <th className="text-left px-3 py-1.5 font-semibold">Longitude SIRGAS2000</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {anm.preview.map((v) => (
                                        <tr key={v.num} className={v.closing ? "bg-slate-50/60" : ""}>
                                            <td className="px-3 py-1 font-medium text-slate-700">{v.pa ? "PA / 1" : v.closing ? `${v.num} (fecha)` : v.num}</td>
                                            <td className="px-3 py-1 font-mono text-slate-600">{v.latDMS}</td>
                                            <td className="px-3 py-1 font-mono text-slate-600">{v.lngDMS}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button onClick={downloadRepemCSV} data-testid="geo-anm-csv"
                                className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
                                <FileSpreadsheet className="w-4 h-4" />CSV ANM/REPEM
                            </button>
                            <button onClick={downloadPackage} data-testid="geo-anm-package"
                                className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
                                <Package className="w-4 h-4" />Baixar pacote completo
                            </button>
                            <span className="text-[11px] text-slate-400 self-center">SHP · KML · GeoJSON · CSV em um ZIP</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
